<template>
  <div class="p-6 flex-1 flex flex-col space-y-6">
    <div class="flex gap-4">
      <!-- Filters Section -->
      <div
        class="flex flex-col space-y-4"
        :class="[highPriorityNotifications?.length > 0 ? 'w-1/2' : 'w-full']"
      >
        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <!-- Search by name/reference -->
            <div class="space-y-1">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSelector.ProjectDashboard.label.search') }}
              </label>

              <AppInput
                v-model="filtersForm.form.search"
                name="search"
                type="text"
                placeholder="Search by name or reference..."
                class="form-input"
              />
            </div>

            <!-- Contract start date -->
            <div class="space-y-1">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSelector.ProjectDashboard.label.contractStartDate') }}
              </label>

              <AppInput
                v-model="filtersForm.form.contract_start_date"
                name="contract_start_date"
                type="date"
                class="form-input"
              />
            </div>

            <!-- Contract end date -->
            <div class="space-y-1">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSelector.ProjectDashboard.label.contractEndDate') }}
              </label>

              <AppInput
                v-model="filtersForm.form.contract_end_date"
                name="contract_end_date"
                type="date"
                class="form-input"
              />
            </div>

            <!-- Financial year -->
            <div class="space-y-1">
              <label class="block text-sm font-medium text-gray-700">
                {{ $t('ProjectSelector.ProjectDashboard.label.financialYear') }}
              </label>

              <AppSelect
                v-model="filtersForm.form.financial_year"
                name="financial_year"
                :options="financialYearOptions"
                :placeholder="$t('ProjectSelector.ProjectDashboard.placeholder.allYears')"
                class="form-input"
              />
            </div>

            <!-- Clear filters -->
            <div class="flex items-end">
              <button
                type="button"
                class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                @click="clearFilters"
              >
                {{ $t('ProjectSelector.ProjectDashboard.button.clearFilters') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Project Summary -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            class="bg-white rounded-lg border border-gray-200 p-6 flex items-center space-x-4 cursor-pointer transition-all duration-200"
            :class="[
              filtersForm.form.status === 'live'
                ? 'border-blue-500 bg-blue-50 shadow-md'
                : 'hover:border-gray-300 hover:shadow-md'
            ]"
            @click="setActiveFilter('live')"
          >
            <div class="p-3 rounded-lg bg-blue-100">
              <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-6 h-6 text-blue-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
</svg>
            </div>

            <div class="flex-1">
              <h3 class="text-sm font-medium text-gray-600">
                {{ $t('ProjectSelector.ProjectDashboard.liveProjects') }}
              </h3>

              <p class="text-2xl font-bold text-gray-900">
                {{ $tn(liveProjectsCount) }}
              </p>
            </div>
          </div>

          <div
            class="bg-white rounded-lg border border-gray-200 p-6 flex items-center space-x-4 cursor-pointer transition-all duration-200"
            :class="[
              filtersForm.form.status === 'archived'
                ? 'border-blue-500 bg-blue-50 shadow-md'
                : 'hover:border-gray-300 hover:shadow-md'
            ]"
            @click="setActiveFilter('archived')"
          >
            <div class="p-3 rounded-lg bg-gray-100">
              <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
     class="w-6 h-6 text-gray-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
    />
</svg>
            </div>

            <div class="flex-1">
              <h3 class="text-sm font-medium text-gray-600">
                {{ $t('ProjectSelector.ProjectDashboard.archivedProjects') }}
              </h3>

              <p class="text-2xl font-bold text-gray-900">
                {{ $tn(archivedProjectsCount) }}
              </p>
            </div>
          </div>

          <div
            class="bg-white rounded-lg border border-gray-200 p-6 flex items-center space-x-4 cursor-pointer transition-all duration-200"
            :class="[
              filtersForm.form.status === 'all'
                ? 'border-blue-500 bg-blue-50 shadow-md'
                : 'hover:border-gray-300 hover:shadow-md'
            ]"
            @click="setActiveFilter('all')"
          >
            <div class="p-3 rounded-lg bg-green-100">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-6 h-6 text-green-600">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
            </div>

            <div class="flex-1">
              <h3 class="text-sm font-medium text-gray-600">
                {{ $t('ProjectSelector.ProjectDashboard.allProjects') }}
              </h3>

              <p class="text-2xl font-bold text-gray-900">
                {{ $tn(totalProjectsCount) }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Notifications section -->
      <div
        v-if="highPriorityNotifications?.length > 0"
        class="w-1/2"
      >
        <div class="bg-white rounded-lg border border-gray-200 p-6 h-full flex flex-col">
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-lg font-semibold text-gray-900">
              Action Needed
            </h3>

            <button
              type="button"
              class="btn btn--primary-blue"
              @click="markAllAsRead"
            >
              Mark all as read
            </button>
          </div>

          <!-- Loading state -->
          <div
            v-if="fetchHighPriorityNotificationsAction.is(States.LOADING) && !highPriorityNotifications?.length"
            class="text-center py-8"
          >
            <div class="flex flex-col items-center">
              <AppSpinner class="h-6 w-6 mb-2" />

              <p class="text-sm text-gray-500">
                Loading notifications...
              </p>
            </div>
          </div>

          <!-- Notifications list -->
          <div
            v-else-if="highPriorityNotifications?.length > 0"
            class="flex flex-col flex-1"
          >
            <div class="relative overflow-y-auto flex-1">
              <div class="absolute inset-0 overflow-y-auto flex flex-col space-y-2">
                <div
                  v-for="notification in highPriorityNotifications"
                  :key="notification.id"
                  class="p-3 rounded-lg border border-gray-200 hover:bg-red-200 transition-colors duration-150"
                  :class="[!notification.read_at ? 'bg-red-50 border-red-200' : '']"
                >
                  <div class="flex items-center justify-between gap-2">
                    <p class="text-sm font-medium text-gray-600">
                      {{ notification.data.project_name }} -
                      <span class="text-sm font-medium text-gray-900 truncate">
                        {{ notification.data.title ?? 'Notification' }}
                      </span>
                    </p>

                    <div
                      v-if="notification.data.popup_component"
                      class="flex-shrink-0"
                    >
                      <button
                        type="button"
                        class="btn btn--tertiary-blue no-underline p-0"
                        @click="openNotificationPopup(notification)"
                      >
                        {{ notification.data.popup_text ?? 'View' }}
                      </button>
                    </div>

                    <div
                      v-else-if="notification.data.action_url"
                      class="flex-shrink-0"
                    >
                      <button
                        type="button"
                        class="btn btn--tertiary-blue no-underline p-0"
                        @click="handleNotificationAction(notification.data.action_url)"
                      >
                        {{ notification.data.action }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Projects Table -->
    <div class="bg-white rounded-lg border border-gray-200 flex flex-col relative flex-1">
      <div class="flex justify-between items-center p-6 border-b border-gray-200">
        <h2 class="text-lg font-semibold text-gray-900">
          {{ $t('ProjectSelector.ProjectDashboard.projects') }}
        </h2>

        <div class="flex space-x-3">
          <button
            v-if="canAddProject"
            type="button"
            class="btn btn--primary-blue flex space-x-2 items-center"
            @click="$emit('newProject')"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 mr-2">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

            <span>
              {{ $t('ProjectSelector.ProjectDashboard.button.newProject') }}
            </span>
          </button>
        </div>
      </div>

      <div class="relative overflow-hidden flex-1">
        <!-- Initial loading -->
        <div
          v-if="infiniteScroll.loading.value && !infiniteScroll.firstLoad.value"
          class="absolute inset-0 overflow-y-auto"
        >
          <div class="text-center py-12">
            <div class="flex flex-col items-center">
              <AppSpinner class="h-8 w-8 mb-4" />

              <h3 class="text-sm font-medium text-gray-900 mb-1">
                {{ $t('ProjectSelector.ProjectDashboard.loadingProjects') }}
              </h3>
            </div>
          </div>
        </div>

        <template v-else-if="infiniteScroll.firstLoad.value">
          <!-- Empty state -->
          <div
            v-if="!infiniteScroll.loading.value && infiniteScroll.items.value.length === 0"
            class="absolute inset-0 overflow-y-auto"
          >
            <div class="text-center py-12">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12 text-gray-400 mx-auto mb-4">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

              <h3 class="text-lg font-medium text-gray-900 mb-2">
                {{ $t('ProjectSelector.ProjectDashboard.noProjectsFound') }}
              </h3>

              <p class="text-gray-500 mb-4">
                Get started by creating your first project.
              </p>

              <button
                v-if="canAddProject"
                type="button"
                class="btn btn--primary-blue flex space-x-2 mx-auto items-center"
                @click="$emit('newProject')"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

                <span>
                  {{ $t('ProjectSelector.ProjectDashboard.button.createNewProject') }}
                </span>
              </button>
            </div>
          </div>

          <!-- Project list -->
          <div
            v-else
            class="absolute inset-0 flex flex-col"
          >
            <ProjectTableHeader class="shrink-0 border-b border-gray-200" />

            <div class="relative min-h-0 flex-1">
              <div
                v-bind="infiniteScroll.containerProps"
                class="h-full"
              >
                <div v-bind="infiniteScroll.wrapperProps.value">
                  <ProjectRow
                    v-for="{ data: row } in infiniteScroll.virtualList.value"
                    :key="row.project.id"
                    :project="row.project"
                    :profit="row.profit"
                    :total-budget="row.total_budget"
                    :total-certified="row.total_certified"
                    :selected="projectsStore.project?.id === row.project.id"
                    class="h-16"
                    @select="selectProject"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>

        <div
          v-else
          class="absolute inset-0 overflow-y-auto"
        >
          <div class="text-center py-12">
            <div class="flex flex-col items-center">
              <AppSpinner class="h-8 w-8 mb-4" />

              <h3 class="text-sm font-medium text-gray-900 mb-1">
                {{ $t('ProjectSelector.ProjectDashboard.loadingProjects') }}
              </h3>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Teleport to="#modals">
      <component
        :is="notificationComponentMap[popupNotification.data.popup_component]"
        v-if="popupNotification"
        :notification="popupNotification"
        @deselect="popupNotification = undefined"
        @reload="onNotificationPopupSuccess"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import ContractEventNotificationPopup from '@/components/layout/notifications/ContractEventNotificationPopup.vue';
import ProjectRow from '@/components/ProjectSelector/ProjectRow.vue';
import ProjectTableHeader from '@/components/ProjectSelector/ProjectTableHeader.vue';
import { useFormData } from '@/composables/useFormData';
import { useInfiniteScroll } from '@/composables/useInfiniteScroll';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { getLinkFromActionUrl } from '@/helpers';
import Project from '@/models/Project';
import { useNotificationsStore } from '@/store/notifications';
import { useProjectsStore } from '@/store/projects';
import { useDebounceFn } from '@vueuse/core';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId?: string;
  canAddProject: boolean;
}>();

const emit = defineEmits<{
  (e: 'expanded', value: boolean);
  (e: 'newProject');
}>();

const projectsStore = useProjectsStore();
const notificationsStore = useNotificationsStore();
const router = useRouter();
// High priority notifications
const highPriorityNotifications = ref<AppNotification[]>([]);
const fetchHighPriorityNotificationsAction = useStoreApiAction(notificationsStore.fetchNotifications);
const popupNotification = ref<AppNotification | undefined>(undefined);

const filtersForm = useFormData({
  search: '',
  contract_start_date: '',
  contract_end_date: '',
  financial_year: undefined,
  status: 'live',
});

// Infinite scroll for projects
const infiniteScroll = useInfiniteScroll<
  { project: Project; profit: number; total_budget: number; total_certified: number }
>({
  fetchFunction: async (cursor?: string) => {
    const response = await projectsStore.searchProjects({
      ...filtersForm.form,
      cursor,
    });

    const projectsWithProfit = response.projects.data.map((project: Project) => {
      const projectProfit = response.profit.find((profitForProject) => profitForProject.id === project.id);

      return {
        project,
        profit: projectProfit.profit,
        total_budget: projectProfit.total_budget,
        total_certified: projectProfit.total_certified,
      };
    });

    return {
      data: projectsWithProfit,
      next_cursor: response.projects.next_cursor,
      prev_cursor: response.projects.prev_cursor,
      has_more: response.has_more,
    };
  },

  immediate: false,
  itemHeight: 64,
  overscan: 10,
});

// Search projects using API with infinite scroll
const searchProjects = async () => {
  infiniteScroll.reset();
  await infiniteScroll.loadMore();
};

const debouncedSearch = useDebounceFn(() => {
  searchProjects();
}, 300);

watch(filtersForm.form, () => {
  debouncedSearch();
}, { immediate: true });

// Computed properties for summary cards - these now need to be fetched separately
const liveProjectsCount = computed(() => {
  return projectsStore.models.whereNull('archived_at').get().length;
});

const archivedProjectsCount = computed(() => {
  return projectsStore.models.whereNotNull('archived_at').get().length;
});

const totalProjectsCount = computed(() => {
  return liveProjectsCount.value + archivedProjectsCount.value;
});

// Financial year options for the select component
const financialYearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  const options = [];

  // Add "All Years" option
  options.push({ value: '', label: 'All Years' });

  // Add years from 5 years ago to 1 year in the future
  for(let year = currentYear - 5;year <= currentYear + 1;year++) {
    options.push({ value: year.toString(), label: year.toString() });
  }

  return options;
});

// Methods
const clearFilters = () => {
  filtersForm.resetData(['search', 'contract_start_date', 'contract_end_date', 'financial_year']);
  infiniteScroll.reset();
  searchProjects();
};

const setActiveFilter = (filter: string) => {
  filtersForm.form.status = filter;
};

const selectProject = (project: Project) => {
  // Select the project
  projectsStore.setProject(project.id, true);

  // Collapse the sidebar by emitting the expanded event with false
  emit('expanded', false);
};

// Load high priority notifications
const loadHighPriorityNotifications = async () => {
  try {
    const paginatedNotifications = await fetchHighPriorityNotificationsAction.request({
      priority_level: 'critical',
      page: 1,
      read: 0,
    });

    highPriorityNotifications.value = paginatedNotifications.data;
  } catch (error) {
    console.error('Failed to load high priority notifications:', error);
  }
};

const notificationComponentMap = {
  'contract-event-notification-popup': ContractEventNotificationPopup,
};

const openNotificationPopup = (notification: AppNotification) => {
  popupNotification.value = notification;
};

const onNotificationPopupSuccess = () => {
  loadHighPriorityNotifications();
  popupNotification.value = undefined;
};

// Handle notification action click
const handleNotificationAction = (url: string) => {
  router.push(getLinkFromActionUrl(url));
};

const markAllAsRead = async () => {
  await notificationsStore.markAllAsRead();

  // Update unread count
  notificationsStore.unreadCount = 0;

  // Refresh the list to get updated read status
  loadHighPriorityNotifications();
};

// Load high priority notifications on mount
onMounted(() => {
  loadHighPriorityNotifications();
});
</script>
